import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import DatePicker from 'react-datepicker';
import { WatchlistDetailNoteType, WatchlistDetailNoteTypeOptions } from '../WatchlistConstants';

export const NoteDetailDialog = ({ noteType = WatchlistDetailNoteType.Loan, note, onSubmit, onCancel }) => {
    const [item, set_item] = useState(null);
    const [noteDate, set_noteDate] = useState(null);
    const [notes, set_notes] = useState('');
    const [noteTypeDisplay, set_noteTypeDisplay] = useState('');

    const styles = {
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { padding: '10px 5px' },
        formRow: { marginBottom: '0' },
    };

    useEffect(() => {
        if (!noteType) {
            return;
        }
        const displayName = WatchlistDetailNoteTypeOptions.find((f) => f.key === noteType)?.display;
        if (!displayName) {
            set_noteTypeDisplay('');
        } else {
            set_noteTypeDisplay(displayName);
        }
    }, [noteType]);

    const handleClose = () => {
        onCancel && onCancel();
    };

    const onFormSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const model = {
            startDate: `${noteDate.getFullYear()}-${(noteDate.getMonth() + 1).toString().padStart(2, '0')}-01`,
            notes,
            virtualId: item.virtualId,
        };

        onSubmit && onSubmit(model);
    };

    useEffect(() => {
        if (note) {
            setState(note);
        } else {
            resetState();
        }
    }, [note]);

    const setState = (note) => {
        if (note.startDate) {
            let dt = new Date(note.startDate);
            dt = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), 1);
            set_noteDate(dt);
        }
        set_notes(note.notes);
        set_item(note);
    };

    const resetState = () => {
        set_noteDate(null);
        set_notes('');
        set_item(null);
    };

    const canSubmit = () => {
        return canEdit() && noteDate && notes;
    };

    const canEdit = () => {
        return !item.isLocked && hasPermission(PermissionType.WatchlistEdit);
    };

    const getMaxDate = () => {
        const today = new Date();
        //If date is 21 or later then end of next month otherwise end of this month
        let maxDate = null;
        if (today.getDay() >= 21) {
            maxDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        } else {
            maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        }
        return maxDate;
    };

    const onDateChanged = (dt) => {
        set_noteDate(dt);
    };

    const noteHeader = () => {
        if (note?.startDate) {
            return noteTypeDisplay ? `Edit ${noteTypeDisplay} Note` : 'Edit Note';
        } else {
            return noteTypeDisplay ? `Add ${noteTypeDisplay} Note` : 'Add Note';
        }
    };

    if (!item) {
        return;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={true}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '600px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>{noteHeader()}</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <form
                                    name='noteForm'
                                    onSubmit={onFormSubmit}
                                >
                                    <div
                                        style={styles.form}
                                        className={'standard-form'}
                                    >
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-6'>
                                                <label htmlFor='noteDate'>
                                                    Month <span className='req'>*</span>
                                                </label>
                                                <DatePicker
                                                    id={'noteDate'}
                                                    selected={noteDate}
                                                    onChange={(value) => onDateChanged(value)}
                                                    required={true}
                                                    dateFormat='yyyy MMM'
                                                    showMonthYearPicker
                                                    showFullMonthYearPicker
                                                    disabled={!canEdit()}
                                                    maxDate={getMaxDate()}
                                                    autoComplete='off'
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-12'>
                                                <label htmlFor='notes'>
                                                    Notes <span className='req'>*</span>
                                                </label>
                                                <textarea
                                                    placeholder='Enter relevant notes for the selected month...'
                                                    id='notes'
                                                    rows={3}
                                                    maxLength={1000}
                                                    value={notes}
                                                    onChange={(e) => set_notes(e.target.value)}
                                                    disabled={!canEdit()}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            type='submit'
                                            className='button primary'
                                            data-dismiss='modal'
                                            disabled={!canSubmit()}
                                        >
                                            Submit
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
